<template>
  <div class="payment-methods-container">
    <div v-if="!hasPaymentProviders">
      <span>{{
        $t('components.paymentMethod.noPaymentProvidersMessage')
      }}</span>
    </div>
    <div v-else>
      <div v-show="showWallet && stripeEnabled">
        <DividerWithText
          :text="$t('components.paymentMethod.prButtonUpperDivider')"
          for="prButton"
        />
        <div
          v-if="isLoadingPrButton"
          class="d-flex justify-center align-center my-5"
          style="height: 52px"
        >
          <v-progress-circular indeterminate size="24" color="black" />
        </div>
        <div v-show="!isLoadingPrButton" ref="prButton" class="my-5" />
        <div v-if="externalGPayAvailable" class="my-5">
          <button
            type="button"
            aria-label="Order with GPay"
            class="gpay-button black order en"
            @click="payWithExternalWallet"
          />
        </div>
        <DividerWithText
          :text="$t('components.paymentMethod.prButtonLowerDivider')"
          for="prButton"
        />
      </div>
      <v-radio-group
        v-model="otherPaymentMethod"
        column
        hide-details
        :class="{
          'mt-0': !showWallet && !stripeEnabled,
        }"
        @change="onPaymentMethodChange"
      >
        <v-radio
          v-if="mollieIdealEnabled"
          :color="color"
          :value="methods.MOLLIE_IDEAL"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-mollie-ideal"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t('components.paymentMethod.idealTitle') }}
                </div>
                <div class="tp-text-label-2">
                  {{ $t('components.paymentMethod.idealDescription') }}
                </div>
              </div>
              <img
                :src="require(`@/assets/images/icons/ideal-icon.svg`)"
                height="20px"
                class="ml-2"
              />
            </div>
          </template>
        </v-radio>
        <v-radio
          v-if="solidWalletEnabled"
          :value="methods.SOLID_WALLET"
          :disabled="solidDisabled"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-solid-wallet"
          :color="color"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t(solidWalletTitle) }}
                </div>
                <div class="tp-text-label-2">
                  {{
                    solidDisabled
                      ? $t('components.paymentMethod.temporarilyUnavailable')
                      : $t('components.paymentMethod.walletDescription')
                  }}
                </div>
              </div>
              <div class="d-flex">
                <img
                  v-if="showApplePay"
                  height="20"
                  :src="require(`@/assets/images/payments/apple-pay.svg`)"
                />
                <img
                  v-if="!showApplePay"
                  class="ml-1"
                  height="20"
                  :src="
                    require(`@/assets/images/payments/${
                      surfaceTone === 'dark'
                        ? 'google-pay-logo-2-dark.svg'
                        : 'google-pay-logo-2.svg'
                    }`)
                  "
                />
              </div>
            </div>
          </template>
        </v-radio>
        <v-expand-transition>
          <v-row
            v-if="otherPaymentMethod === methods.SOLID_WALLET && !solidDisabled"
            no-gutters
          >
            <v-col cols="12">
              <SolidGateWallet
                v-if="solidGatePaymentIntentSignature"
                @on-success="onSuccessfulPayment"
                @on-error="onError"
                @on-submit="onSubmit"
              />
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-radio
          v-if="solidCardEnabled"
          :value="methods.SOLID_CARD"
          :disabled="solidDisabled"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-solid-card"
          :color="color"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t('components.paymentMethod.cardTitle') }}
                </div>
                <div class="tp-text-label-2">
                  {{
                    solidDisabled
                      ? $t('components.paymentMethod.temporarilyUnavailable')
                      : $t('components.paymentMethod.cardDescription')
                  }}
                </div>
              </div>
              <div class="d-flex">
                <img :src="require(`@/assets/images/payments/visa.svg`)" />
                <img
                  class="pl-1"
                  :src="require(`@/assets/images/payments/mastercard.svg`)"
                />
              </div>
            </div>
          </template>
        </v-radio>
        <v-expand-transition>
          <v-row
            v-if="otherPaymentMethod === methods.SOLID_CARD && !solidDisabled"
            no-gutters
          >
            <v-col cols="12">
              <SolidGateCard
                v-if="solidGatePaymentIntentSignature"
                ref="solidCardForm"
                @on-success="onSuccessfulPayment"
                @on-error="onError"
                @on-submit="onSubmit"
              />
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-radio
          v-if="neopayBlikEnabled"
          :color="color"
          :value="methods.NEOPAY_BLIK"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-neopay-blik"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t('components.paymentMethod.blikTitle') }}
                </div>
                <div class="tp-text-label-2">
                  {{ $t('components.paymentMethod.blikDescription') }}
                </div>
              </div>
              <img
                :src="require(`@/assets/images/payments/blik-logo.svg`)"
                width="40"
              />
            </div>
          </template>
        </v-radio>
        <v-expand-transition>
          <v-row
            v-show="[methods.NEOPAY_BLIK].includes(otherPaymentMethod)"
            no-gutters
          >
            <v-col cols="12">
              <div class="tp-text-body-gray">
                {{ $t('components.paymentMethod.blikSubtitle') }}
              </div>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-radio
          v-if="montonioEnabled"
          :color="color"
          :value="methods.MONTONIO"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-montonio"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div class="d-flex justify-space-between align-center w-100">
                <div>
                  <div class="tp-text-subtitle-medium">
                    {{ $t('components.paymentMethod.banklinkTitle') }}
                  </div>
                  <div class="tp-text-label-2">
                    {{ $t('components.paymentMethod.banklinkDescription') }}
                  </div>
                </div>
                <img :src="require(`@/assets/images/icons/bank-icon.svg`)" />
              </div>
            </div>
          </template>
        </v-radio>
        <v-radio
          v-if="neopayEnabled"
          :color="color"
          :value="methods.NEOPAY"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-neopay"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t('components.paymentMethod.banklinkTitle') }}
                </div>
                <div class="tp-text-label-2">
                  {{ $t('components.paymentMethod.banklinkDescription') }}
                </div>
              </div>
              <img :src="require(`@/assets/images/icons/bank-icon.svg`)" />
            </div>
          </template>
        </v-radio>
        <v-expand-transition>
          <v-row
            v-show="
              [methods.MONTONIO, methods.NEOPAY].includes(
                otherPaymentMethod
              )
            "
            no-gutters
          >
            <v-col cols="12">
              <div class="tp-text-body-gray">
                {{ $t('components.paymentMethod.montonioSubtitle') }}
              </div>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-radio
          v-if="stripeEnabled"
          :value="methods.STRIPE"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-stripe-card"
          :color="color"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div>
                <div class="tp-text-subtitle-medium">
                  {{ $t('components.paymentMethod.cardTitle') }}
                </div>
                <div class="tp-text-label-2">
                  {{ $t('components.paymentMethod.cardDescription') }}
                </div>
              </div>
              <div class="d-flex">
                <img :src="require(`@/assets/images/payments/visa.svg`)" />
                <img
                  class="pl-1"
                  :src="require(`@/assets/images/payments/mastercard.svg`)"
                />
              </div>
            </div>
          </template>
        </v-radio>
        <v-expand-transition>
          <v-row v-show="otherPaymentMethod === methods.STRIPE" no-gutters>
            <v-col cols="12">
              <div ref="card" class="mb-3 mx-1"></div>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-radio
          v-if="cashEnabled"
          :color="color"
          :value="methods.CASH"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-cash"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div class="d-flex justify-space-between align-center w-100">
                <div>
                  <div class="tp-text-subtitle-medium">
                    {{ $t('components.paymentMethod.cashTitle') }}
                  </div>
                  <div class="tp-text-label-2">
                    {{ $t('components.paymentMethod.cashDescription') }}
                  </div>
                </div>
                <v-icon color="rgba(0, 0, 0, 0.7)">mdi-cash</v-icon>
              </div>
            </div>
          </template>
        </v-radio>
        <v-radio
          v-if="roomBillEnabled"
          :color="color"
          :value="methods.ROOM_BILL"
          class="py-3"
          :ripple="false"
          data-cy="payment-method-room-bill"
        >
          <template v-slot:label>
            <div class="d-flex justify-space-between align-center w-100">
              <div class="d-flex justify-space-between align-center w-100">
                <div>
                  <div class="tp-text-subtitle-medium">
                    {{ $t('components.paymentMethod.roomBillTitle') }}
                  </div>
                  <div class="tp-text-label-2">
                    {{ $t('components.paymentMethod.roomBillDescription') }}
                  </div>
                </div>
                <v-icon color="rgba(0, 0, 0, 0.7)">mdi-home-outline</v-icon>
              </div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </div>
  </div>
</template>

<script>
import DividerWithText from '@/components/common/DividerWithText.vue';
import SolidGateCard from '@/components/Payments/SolidGateCard.vue';
import SolidGateWallet from '@/components/Payments/SolidGateWallet.vue';
import { PAYMENT_METHODS } from '@/utils/constants';
import { applePayAvailable } from '@/utils/paymentMethods';
import { resolveSurfaceTone } from '@/helpers/lightOrDark.js';

export default {
  components: {
    DividerWithText,
    SolidGateCard,
    SolidGateWallet,
  },
  props: {
    walletAvailable: {
      type: Boolean,
      default: false,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    paymentMethodsOverride: {
      type: Array,
      default: null,
    },
    clientSecret: {
      type: String,
      default: null,
    },
    preselectedPaymentMethod: {
      type: String,
      default: null,
    },
    isLoadingPrButton: {
      type: Boolean,
      default: false,
    },
    // Greys out Solidgate methods (e.g. during a provider incident) while
    // keeping the rest of the list interactive.
    solidDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      otherPaymentMethod: null,
      isSettingInitialMethod: false,
      surfaceTone: 'light',
    };
  },
  computed: {
    color() {
      return 'black';
    },
    methods() {
      return PAYMENT_METHODS;
    },
    hasPaymentProviders() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.length > 0;
      }

      return this.$store.getters.hasPaymentProviders;
    },
    showWallet() {
      return this.walletAvailable || this.externalGPayAvailable;
    },
    stripeEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.STRIPE);
      }
      return this.$store.getters.paymentProviderStripeEnabled;
    },
    montonioEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.MONTONIO);
      }
      return this.$store.getters.paymentProviderMontonioEnabled;
    },
    neopayEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.NEOPAY);
      }
      return this.$store.getters.paymentProviderNeopayEnabled;
    },
    neopayBlikEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.NEOPAY_BLIK);
      }
      return this.$store.getters.paymentProviderNeopayBlikEnabled;
    },
    mollieIdealEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.MOLLIE_IDEAL);
      }
      return this.$store.getters.paymentProviderMollieIdealEnabled;
    },
    externalGPayAvailable() {
      // return false;
      /**
       * Temporarily disable external google pay due to app compatibility issues
       */
      return this.$store.getters.externalGPayWalletAvailable;
    },
    solidEnabled() {
      return this.$store.getters.paymentProviderSolidEnabled;
    },
    solidCardEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.SOLID_CARD);
      }
      return this.solidEnabled;
    },
    solidWalletEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.SOLID_WALLET);
      }
      return this.solidEnabled;
    },
    cashEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.CASH);
      }

      return this.$store.getters.paymentProviderCashEnabled;
    },
    roomBillEnabled() {
      if (this.paymentMethodsOverride) {
        return this.paymentMethodsOverride.includes(this.methods.ROOM_BILL);
      }

      return this.$store.getters.paymentProviderRoomBillEnabled;
    },
    solidGatePaymentIntentSignature() {
      return this.$store.getters.solidGatePaymentIntentSignature;
    },
    showApplePay() {
      return applePayAvailable();
    },
    solidWalletTitle() {
      if (this.showApplePay) {
        return this.$t('components.paymentMethod.walletTitleApple');
      }

      return this.$t('components.paymentMethod.walletTitleGoogle');
    },
  },
  mounted() {
    // Google branding requires the white-text acceptance mark on dark surfaces.
    this.surfaceTone = resolveSurfaceTone(this.$el);

    if (this.preselectedPaymentMethod) {
      this.isSettingInitialMethod = true;
      this.otherPaymentMethod = this.preselectedPaymentMethod;
      this.$emit('on-change', this.otherPaymentMethod, {
        userInitiated: false,
      });
      this.$nextTick(() => {
        this.isSettingInitialMethod = false;
      });
    }
  },
  methods: {
    onPaymentMethodChange() {
      this.$emit('on-change', this.otherPaymentMethod, {
        userInitiated: !this.isSettingInitialMethod,
      });
    },
    payWithExternalWallet() {
      // ASG INTEGRATION
      this.$store.dispatch('sendStripeClientSecret', this.clientSecret);
    },
    onSuccessfulPayment(data) {
      this.$emit('on-success', data);
    },
    onError(error) {
      this.$emit('on-error', error);
    },
    onSubmit(data) {
      this.$emit('on-submit', data);
    },
  },
};
</script>

<style lang="scss" scoped>
.payment-methods-container {
  overflow-anchor: none;
}

.w-100 {
  width: 100%;
}

// Typography classes set an explicit text color, so Vuetify's disabled
// state alone does not gray out the label. Mute text and logos here.
.v-radio.v-radio--is-disabled ::v-deep .v-label {
  div {
    color: rgba(0, 0, 0, 0.38) !important;
  }

  img {
    opacity: 0.4;
  }
}

.gpay-button {
  background-origin: content-box;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  border: 0px;
  border-radius: 4px;
  box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 1px 0px,
    rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
  cursor: pointer;
  height: 40px;
  min-height: 40px;
  padding: 12px 24px 10px;
  min-width: 240px;
  width: 100%;
}

.gpay-button.black {
  background-color: #000;
  box-shadow: none;
}

.gpay-button.black.order.en {
  background-image: url(https://www.gstatic.com/instantbuy/svg/dark/order/en.svg);
  min-width: 170px;
}
</style>
